import { Component, Input, OnInit } from '@angular/core';
import { JwtAuthService } from '../../services/auth/jwt-auth.service';
import { OrganizationService } from '../../services/organization.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { UrlService } from '../../services/url.service';
import { Person } from '../../../state/models/person';

@Component({
    selector: 'page-toolbar',
    template: `
    <mat-toolbar showOnSize="desktop" marLayout="row" flexAlignContent="space-between">
      <h1 marFlex="100">{{ title }}</h1>
      <div class="page-toolbar__actions">
        @if (organizationLogo) {
          <a
            class="page-toolbar__company"
            [routerLink]="userOrganizationUrl"
            title="Ir para a empresa principal"
          >
            <img class="page-toolbar__logo" [src]="organizationLogo" alt="Empresa principal" />
            <div class="page-toolbar__company-info">
              <span class="page-toolbar__company-name">{{ organizationName }}</span>
            </div>
          </a>
          <span class="page-toolbar__divider"></span>
        }
        @if (userName) {
          <div class="page-toolbar__user">
            <div class="page-toolbar__avatar">{{ userInitials }}</div>
            <div class="page-toolbar__user-info">
              <a
                class="page-toolbar__user-name"
                [routerLink]="userProfileUrl"
                title="Ver meu perfil"
              >{{ userName }}</a>
              <span class="page-toolbar__user-status">Logado</span>
            </div>
          </div>
        }
        <support-button></support-button>
      </div>
    </mat-toolbar>
    `,
    styles: `
    .page-toolbar__actions {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .page-toolbar__company {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      text-decoration: none;
    }

    .page-toolbar__logo {
      height: 32px;
      width: auto;
      object-fit: contain;
    }

    .page-toolbar__company-info {
      display: flex;
      flex-direction: column;
      line-height: 1.2;
    }

    .page-toolbar__company-name {
      font-size: 14px;
      font-weight: 600;
      color: #2c2c2c;
    }

    .page-toolbar__company-status {
      font-size: 12px;
      color: #7a7a7a;
    }

    .page-toolbar__divider {
      display: block;
      flex-shrink: 0;
      width: 1px;
      min-width: 1px;
      height: 36px;
      background: #d9d9d9;
    }

    .page-toolbar__user {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .page-toolbar__avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #495B72;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    .page-toolbar__user-info {
      display: flex;
      flex-direction: column;
      line-height: 1.2;
    }

    .page-toolbar__user-name {
      font-size: 14px;
      font-weight: 600;
      color: #2c2c2c;
      text-decoration: none;
      cursor: pointer;
    }

    .page-toolbar__user-name:hover {
      text-decoration: underline;
    }

    .page-toolbar__user-status {
      font-size: 12px;
      color: #7a7a7a;
    }

    /* Contexto de fundo azul (operadora): garante legibilidade sobre o #495B72 */
    :host-context(.bg-blue) {
      .page-toolbar__divider {
        background: rgba(255, 255, 255, 0.6);
      }

      .page-toolbar__avatar {
        background: #fff;
        color: #495B72;
      }

      .page-toolbar__user-name,
      .page-toolbar__company-name {
        color: #fff;
      }

      .page-toolbar__user-status,
      .page-toolbar__company-status {
        color: rgba(255, 255, 255, 0.75);
      }
    }
  `,
    standalone: false
})
export class PageToolbarComponent implements OnInit {
  @Input() title: string;

  user: Person;
  organizationLogo: string = '';
  organizationName: string = '';
  userOrganizationUrl: string = '';
  userProfileUrl: string = '';

  constructor(
    private jwtAuthService: JwtAuthService,
    private organizationService: OrganizationService,
    private featureFlagService: FeatureFlagService,
    private urlService: UrlService,
  ) {
    this.user = this.jwtAuthService.getUser();
  }

  ngOnInit() {
    this.userProfileUrl = this.urlService.organizationsProfile(
      this.organizationService.getOrganization()?.id
    );

    if (!this.featureFlagService.hasFlagEnable(['PAINEL_WHITELABEL_LOGO'])) {
      return;
    }

    const userOrganizationId = this.organizationService.getUserDirectOrganizationId();
    const visitedOrganization = this.organizationService.getOrganization();

    // Só exibe o logo quando o usuário está visitando uma organização filha da sua
    // própria organização (parent da visitada == org do usuário no authStatement).
    const isVisitingChildOrganization =
      !!userOrganizationId &&
      visitedOrganization?.id !== userOrganizationId &&
      visitedOrganization?.parent?.id === userOrganizationId;

    if (!isVisitingChildOrganization) {
      return;
    }

    this.organizationService.getUserDirectOrganization().subscribe({
      next: organization => {
        if (!organization) {
          return;
        }
        this.organizationLogo = organization.logo ?? organization.parent?.logo ?? '';
        this.organizationName = organization.name ?? '';
        this.userOrganizationUrl = this.organizationService.getDefaultUrl(organization);
      }
    });
  }

  get userName(): string {
    return this.user?.personProperties?.name || '';
  }

  get userInitials(): string {
    const name = this.userName.trim();
    if (!name) {
      return '';
    }
    const parts = name.split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }
}
